'use client';

import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react';
import { useTranslations } from 'next-intl';
import React, { useCallback, useMemo, useState } from 'react';

import ComingSoonTabPanel from '@/app/[locale]/[domain]/(default-layout)/_components/home-section-one/ComingSoonTabPanel';
import InCinemaTabPanel from '@/app/[locale]/[domain]/(default-layout)/_components/home-section-one/InCinemaTabPanel';
import SelectedMovieView from '@/app/[locale]/[domain]/(default-layout)/_components/SelectedMovieView';
import SliderImages from '@/app/[locale]/[domain]/(default-layout)/_components/SliderImages';

import DefaultTabButton from '@/components/DefaultTabButton';
import TabListLine from '@/components/misc/TabListLine';

import { WebsiteHeroSectionTab } from '@cineamo/api/models/website-configuration/WebsiteHeroSectionTab.enum';

import { ApiResponse } from '@cineamo/api/types/ApiResponse.types';

import { cn } from '@cineamo/ui/src/helpers/cn';

import { CachedContentData } from '@/hooks/useCachedContentData';

import TwoColumnsOnLg from '@cineamo/ui/src/layout-components/TwoColumnsOnLg';

import CenterLayout from '@cineamo/composables/src/CenterLayout';
import MobileOrDesktopRenderer from '@cineamo/composables/src/MobileOrDesktopRenderer';
import { useAnalytics } from '@cineamo/composables/src/providers/analyticsProvider';

import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';

type HomeSectionOneContentProps = {
    moviesInCinemas?: CachedContentData[];
    moviesComingSoonResponse?: ApiResponse<unknown, { 'cinema-content-upcomings': CachedContentData[] }>;
};

type SelectedTab = 'inCinemas' | 'upcoming' | 'comingSoon';

const HomeSectionOneContent: React.FC<HomeSectionOneContentProps> = ({ moviesInCinemas, moviesComingSoonResponse }) => {
    const domainConfiguration = useDomainConfiguration();
    const { track } = useAnalytics();
    const heroSectionTabs = domainConfiguration.websiteConfiguration?.homePageHeroSectionTabs;
    const isInCinemasVisible =
        heroSectionTabs?.find((t) => t.section === WebsiteHeroSectionTab.IN_CINEMAS)?.isVisible !== false;
    const isComingSoonVisible =
        heroSectionTabs?.find((t) => t.section === WebsiteHeroSectionTab.COMING_SOON)?.isVisible !== false;
    const showMoviesInCinemas = (moviesInCinemas?.length ?? 0) > 0 && isInCinemasVisible;
    const showMoviesComingSoon =
        (moviesComingSoonResponse?._embedded?.['cinema-content-upcomings']?.length ?? 0) > 0 && isComingSoonVisible;

    const t = useTranslations();

    // Render-time state sync: update selectedTab when data availability changes
    const initialTab: SelectedTab | null = showMoviesInCinemas
        ? 'inCinemas'
        : showMoviesComingSoon
          ? 'comingSoon'
          : null;
    const [prevShowMoviesInCinemas, setPrevShowMoviesInCinemas] = useState(showMoviesInCinemas);
    const [prevShowMoviesComingSoon, setPrevShowMoviesComingSoon] = useState(showMoviesComingSoon);
    const [selectedTab, setSelectedTab] = useState<'inCinemas' | 'upcoming' | 'comingSoon' | null>(initialTab);
    if (prevShowMoviesInCinemas !== showMoviesInCinemas || prevShowMoviesComingSoon !== showMoviesComingSoon) {
        setPrevShowMoviesInCinemas(showMoviesInCinemas);
        setPrevShowMoviesComingSoon(showMoviesComingSoon);
        if (showMoviesInCinemas) {
            setSelectedTab('inCinemas');
        } else if (showMoviesComingSoon) {
            setSelectedTab('comingSoon');
        }
    }

    const [isAutoScrollingEnabled, setIsAutoScrollingEnabled] = useState(true);
    const [currentContent, setCurrentContent] = useState<CachedContentData | null>(null);
    const [nextContent, setNextContent] = useState<CachedContentData | null>(null);
    const [isFading, setIsFading] = useState(false);

    const visibleTabs = useMemo(() => {
        if (heroSectionTabs?.length) {
            const tabMapping: Record<string, { key: SelectedTab; show: boolean }> = {
                in_cinemas: { key: 'inCinemas', show: showMoviesInCinemas },
                coming_soon: { key: 'comingSoon', show: showMoviesComingSoon }
            };
            return heroSectionTabs
                .map((tab) => tabMapping[tab.section])
                .filter((tab) => tab?.show)
                .map((tab) => tab.key);
        }
        return [showMoviesInCinemas && 'inCinemas', showMoviesComingSoon && 'comingSoon'].filter(
            (tab): tab is SelectedTab => !!tab
        );
    }, [showMoviesInCinemas, showMoviesComingSoon, heroSectionTabs]);

    const handleEventTabsChange = useCallback(
        (tabIndex: number) => {
            const tab = visibleTabs[tabIndex];
            setSelectedTab(tab as SelectedTab);
            setIsAutoScrollingEnabled(true);

            track('Homepage Section Shown', {
                section: tab
            });
        },
        [visibleTabs, track]
    );

    const selectedIndex = useMemo(() => {
        return selectedTab ? visibleTabs.indexOf(selectedTab) : -1;
    }, [selectedTab, visibleTabs]);

    return (
        <div
            className="relative"
            role="group"
            aria-label={t('home.in-cinemas') + ' + ' + t('home.coming-soon')}
            onMouseOver={() => setIsAutoScrollingEnabled(false)}
            onMouseOut={() => setIsAutoScrollingEnabled(true)}
            onMouseLeave={() => setIsAutoScrollingEnabled(true)}>
            <TabGroup
                onChange={handleEventTabsChange}
                selectedIndex={selectedIndex}
                manual>
                <div className="relative flex flex-col w-full min-h-240 md:min-h-500 xl:aspect-[1440/440] bg-cover bg-center bg-no-repeat bg-[var(--hero-background-color)]">
                    <MobileOrDesktopRenderer
                        mobileContent={
                            <div className="absolute top-0 bottom-0 right-0 left-0 bg-cover bg-center bg-no-repeat gradient-mask-b-0">
                                <SliderImages
                                    showSelectedMovie={isFading}
                                    selectedContent1={currentContent ?? undefined}
                                    selectedContent2={nextContent ?? undefined}
                                    useKenBurstZoom
                                />
                            </div>
                        }
                        desktopContent={
                            <div className="absolute top-0 bottom-0 right-0 left-0 bg-cover bg-center bg-no-repeat gradient-mask-l-0">
                                <SliderImages
                                    showSelectedMovie={isFading}
                                    selectedContent1={currentContent ?? undefined}
                                    selectedContent2={nextContent ?? undefined}
                                    useKenBurstZoom
                                />
                            </div>
                        }
                    />

                    {/* Header Movie Background */}
                    <div className={cn('relative flex flex-col w-full h-240 md:min-h-400 flex-auto pb-16 md:pb-0')}>
                        <div className="flex flex-col flex-auto">
                            <CenterLayout className="flex flex-col flex-auto">
                                <TwoColumnsOnLg
                                    className="flex-auto w-full"
                                    left={
                                        <div className="flex flex-col flex-auto relative w-full justify-end md:justify-center">
                                            {currentContent && (
                                                <SelectedMovieView
                                                    className={cn(
                                                        'absolute left-0 flex flex-col w-full',
                                                        'transition-all duration-500',
                                                        isFading ? 'opacity-100' : 'opacity-0 pointer-events-none'
                                                    )}
                                                    content={currentContent}
                                                />
                                            )}

                                            {nextContent && (
                                                <SelectedMovieView
                                                    className={cn(
                                                        'flex flex-col w-full',
                                                        'transition-all duration-500',

                                                        !isFading ? 'opacity-100' : 'opacity-0 pointer-events-none'
                                                    )}
                                                    content={nextContent}
                                                />
                                            )}
                                        </div>
                                    }
                                    right={<></>}
                                    leftExtraClass={cn('flex-auto lg:flex-none items-center lg:w-1/3')}
                                    rightExtraClass="lg:!w-2/3"
                                />
                            </CenterLayout>
                        </div>
                    </div>

                    {/* Tabs */}
                    <CenterLayout className="relative lg:absolute h-auto z-20 bottom-0 left-0 right-0 flex flex-col">
                        <div className="w-full">
                            <TabList className="relative">
                                <div className="flex flex-row space-x-[32px]">
                                    {visibleTabs.map((tab) => (
                                        <Tab
                                            key={tab}
                                            as="div">
                                            {({ selected }) => (
                                                <DefaultTabButton
                                                    label={
                                                        tab === 'inCinemas'
                                                            ? t('home.in-cinemas')
                                                            : t('home.coming-soon')
                                                    }
                                                    selected={selected}
                                                />
                                            )}
                                        </Tab>
                                    ))}
                                </div>
                                <TabListLine />
                            </TabList>
                        </div>
                    </CenterLayout>
                </div>
                <div className="flex pt-12 lg:pt-24">
                    <TabPanels className="w-full">
                        {visibleTabs.map((tab) =>
                            tab === 'inCinemas' ? (
                                <TabPanel
                                    key={tab}
                                    unmount={false}>
                                    <InCinemaTabPanel
                                        moviesInCinemas={moviesInCinemas}
                                        onSelectedContentChange={(content1, content2, current) => {
                                            setCurrentContent(content1);
                                            setNextContent(content2);
                                            setIsFading(current);
                                        }}
                                        isAutoScrollingEnabled={selectedTab === 'inCinemas' && isAutoScrollingEnabled}
                                        isEnabled={selectedTab === 'inCinemas'}
                                        onPointerDown={() => setIsAutoScrollingEnabled(false)}
                                    />
                                </TabPanel>
                            ) : (
                                <TabPanel
                                    key={tab}
                                    unmount={false}>
                                    <ComingSoonTabPanel
                                        moviesComingSoonResponse={moviesComingSoonResponse}
                                        onSelectedContentChange={(current, next, isFading) => {
                                            setCurrentContent(current);
                                            setNextContent(next);
                                            setIsFading(isFading);
                                        }}
                                        isAutoScrollingEnabled={selectedTab === 'comingSoon' && isAutoScrollingEnabled}
                                        isEnabled={selectedTab === 'comingSoon'}
                                        onPointerDown={() => setIsAutoScrollingEnabled(false)}
                                    />
                                </TabPanel>
                            )
                        )}
                    </TabPanels>
                </div>
            </TabGroup>
        </div>
    );
};

export default HomeSectionOneContent;
